import { useEffect, useMemo, useState } from "react";
import { styles } from "../styles";
import { storage, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { createServiceRecord } from "../models/models";
import { getNextPmDate } from "../utils/pm";
import { serviceTagOptions } from "../utils/constants";
import { hasCapability } from "../utils/capabilities";
import {
  isCameraNative,
  takePhotoNative,
  pickPhotosNative,
  takePhotoPromptNative,
} from "../utils/photoPicker";
import HighlightText from "../components/HighlightText";
import AuditTrail from "../components/AuditTrail";
import BackButton from "../components/BackButton";
import TypedConfirmModal from "../components/TypedConfirmModal";

export default function EquipmentDetailPage({
  customer,
  location,
  equipment,
  actions,
  user,
  role,
  orgId,
  orgType,
  capabilities,
  setSelectedEquipment,
  setWarrantiesForEquipment,
  onBack,
}) {
  // Always source from the live customer prop; never fall back to the
  // snapshot stored at click-time, otherwise a status change made here
  // would show as stale when the user navigates back into this page.
  const currentLocation =
    customer?.locations?.find((l) => l.id === location?.id) || null;
  const currentEquipment =
    currentLocation?.equipment?.find((e) => e.id === equipment?.id) || null;

  const canEdit = role === "admin" || role === "manager";
  const canViewAudit = role === "admin" || role === "manager";
  const isAdmin = role === "admin";
  const canSetStatus = hasCapability(role, capabilities, "editEquipment");

  const statusMeta = {
    green: { label: "Good", color: "#1a7f37", bg: "#c9efc9" },
    yellow: { label: "Caution", color: "#9a6b00", bg: "#fff3cd" },
    red: { label: "Disabled", color: "#c12c2c", bg: "#f8d7da" },
  };
  const currentStatus = currentEquipment?.status || "green";
  const currentStatusNote = currentEquipment?.statusNote || "";

  const [savingStatus, setSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState("");

  const handleSetStatus = async (newStatus) => {
    if (newStatus === currentStatus) return;
    let note = "";
    if (newStatus === "yellow" || newStatus === "red") {
      note = window.prompt(
        `Note required when setting status to ${statusMeta[newStatus].label}. What's wrong / what's needed?`,
        currentStatusNote
      );
      if (note === null) return;
      if (!note.trim()) {
        setStatusError("A note is required when going to yellow or red.");
        return;
      }
    }
    try {
      setSavingStatus(true);
      setStatusError("");
      await actions.updateEquipmentStatus(
        currentEquipment.id,
        newStatus,
        note.trim()
      );
    } catch (e) {
      setStatusError(e.message);
    } finally {
      setSavingStatus(false);
    }
  };

  // Contractors the facility can send a request to. Sources from the
  // customer doc's memberOrgs (the authoritative cross-org list, written
  // by acceptBuildingInvite) and unions in any legacy building.memberOrgs.
  const linkedOrgInfo = {
    ...(customer?.linkedOrgInfo || {}),
    ...(currentLocation?.linkedOrgInfo || {}),
  };
  const contractorOptions = useMemo(() => {
    const set = new Set([
      ...(customer?.memberOrgs || []),
      ...(currentLocation?.memberOrgs || []),
    ]);
    return [...set]
      .filter((o) => o !== orgId)
      .map((o) => ({
        orgId: o,
        name: linkedOrgInfo[o]?.name || `Contractor ${o.slice(0, 6)}`,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, currentLocation, orgId]);
  const hasContractor = contractorOptions.length > 0;
  const canRequestService =
    orgType === "facility" &&
    hasContractor &&
    hasCapability(role, capabilities, "requestService");

  const [requesting, setRequesting] = useState(false);
  const [requestNote, setRequestNote] = useState("");
  const [requestMsg, setRequestMsg] = useState("");
  const [showRequest, setShowRequest] = useState(false);
  // Multi-contractor selection — same pattern as the Facility quote
  // request form (competitive bidding). Defaults to a single picker;
  // auto-fills when there's exactly one linked contractor.
  const [reqContractorOrgIds, setReqContractorOrgIds] = useState([""]);
  useEffect(() => {
    if (contractorOptions.length === 1) {
      setReqContractorOrgIds((prev) =>
        prev.length === 1 && prev[0] === contractorOptions[0].orgId
          ? prev
          : [contractorOptions[0].orgId]
      );
    }
  }, [contractorOptions]);
  const setContractorAt = (idx, val) =>
    setReqContractorOrgIds((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  const addContractorRow = () =>
    setReqContractorOrgIds((prev) => [...prev, ""]);
  const removeContractorRow = (idx) =>
    setReqContractorOrgIds((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length === 0 ? [""] : next;
    });
  const otherPicks = (idx) =>
    new Set(reqContractorOrgIds.filter((id, i) => i !== idx && id));

  const requestService = async () => {
    const selectedContractors = reqContractorOrgIds.filter(Boolean);
    if (selectedContractors.length === 0) {
      setRequestMsg("Pick at least one contractor to send this to.");
      return;
    }
    setRequesting(true);
    setRequestMsg("");
    try {
      const fn = httpsCallable(functions, "requestService");
      // Independent request per contractor — same pattern as the quote
      // request flow. Each contractor gets their own job in their org.
      for (const contractorOrgId of selectedContractors) {
        await fn({
          buildingId: currentLocation.id,
          equipmentId: currentEquipment.id,
          equipmentName: currentEquipment.type,
          note: requestNote.trim(),
          targetContractorOrgId: contractorOrgId,
        });
      }
      setRequestMsg(
        selectedContractors.length === 1
          ? "Service requested."
          : `Service requested from ${selectedContractors.length} contractors.`
      );
      setRequestNote("");
      setReqContractorOrgIds(
        contractorOptions.length === 1 ? [contractorOptions[0].orgId] : [""]
      );
      setShowRequest(false);
    } catch (err) {
      setRequestMsg(`Couldn't request service: ${err.message}`);
    } finally {
      setRequesting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const [serviceNote, setServiceNote] = useState("");
  const [serviceTag, setServiceTag] = useState("");
  const [servicePhotos, setServicePhotos] = useState([]);
  const [serviceFiles, setServiceFiles] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [editingServiceRecordId, setEditingServiceRecordId] = useState(null);
  const [editServiceNote, setEditServiceNote] = useState("");
  const [editServiceTag, setEditServiceTag] = useState("");

  const [serviceSearch, setServiceSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [showDeletedRecords, setShowDeletedRecords] = useState(false);
  const [showStartupInstructions, setShowStartupInstructions] = useState(false);
  const [showPartsList, setShowPartsList] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState({});

  const [partsListPhotoUpload, setPartsListPhotoUpload] = useState([]);
  const [partsListFileUpload, setPartsListFileUpload] = useState([]);
  const [uploadingPartsList, setUploadingPartsList] = useState(false);

  const addServiceRecord = async () => {
    if (!serviceNote && servicePhotos.length === 0) return;

    try {
      setUploadingPhoto(true);
      setErrorMessage("");

      const newRecord = {
        ...createServiceRecord(serviceNote),
        tag: serviceTag,
        photos: [],
        files: [],
        createdAt: new Date().toISOString(),
        createdBy: user?.email || "unknown",
        updatedAt: new Date().toISOString(),
        updatedBy: "user",
        deleted: false,
        deletedAt: null,
        deletedBy: null,
      };

      const uploadedPhotos = [];
      for (const file of servicePhotos) {
        const filePath = `organizations/${orgId}/serviceRecordPhotos/${customer.id}/${currentLocation.id}/${currentEquipment.id}/${newRecord.id}/${Date.now()}-${file.name}`;
        const photoRef = ref(storage, filePath);
        await uploadBytes(photoRef, file);
        const photoUrl = await getDownloadURL(photoRef);
        uploadedPhotos.push({ url: photoUrl, path: filePath, name: file.name });
      }

      const uploadedFiles = [];
      for (const file of serviceFiles) {
        const filePath = `organizations/${orgId}/serviceFiles/${customer.id}/${currentLocation.id}/${currentEquipment.id}/${newRecord.id}/${Date.now()}-${file.name}`;
        const fileRef = ref(storage, filePath);
        await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(fileRef);
        uploadedFiles.push({ url: fileUrl, path: filePath, name: file.name });
      }

      const finalRecord = {
        ...newRecord,
        photos: uploadedPhotos,
        files: uploadedFiles,
      };

      await actions.addServiceRecord(
        customer.id,
        currentLocation.id,
        currentEquipment.id,
        finalRecord
      );

      setServiceNote("");
      setServicePhotos([]);
      setServiceFiles([]);
      setServiceTag("");

      const pdfInput = document.getElementById("service-file-input");
      if (pdfInput) pdfInput.value = "";
      const photoInput = document.getElementById("service-photo-input");
      if (photoInput) photoInput.value = "";
    } catch (error) {
      console.error("Error adding service record:", error);
      setErrorMessage(error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const updateServiceRecord = async () => {
    if (!editServiceNote || !editingServiceRecordId) return;

    const record = (currentEquipment?.serviceRecords || []).find(
      (r) => r.id === editingServiceRecordId
    );

    await actions.updateServiceRecord(editingServiceRecordId, {
      notes: editServiceNote,
      tag: editServiceTag,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.email || "unknown",
      auditTrail: [
        ...(record?.auditTrail || []),
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          action: "Edited Service Record",
          details: `Notes — Before: ${record?.notes || ""}\nAfter: ${editServiceNote || ""}\nType — Before: ${record?.tag || "-"}\nAfter: ${editServiceTag || "-"}`,
          user: user?.email || "unknown",
          timestamp: new Date().toISOString(),
        },
      ],
    });

    // If the category was changed TO Maintenance (and wasn't already),
    // bump the equipment's Last PM Date to this record's service date —
    // so fixing a miscategorised record updates PM tracking too.
    if (editServiceTag === "Maintenance" && record?.tag !== "Maintenance") {
      const pmDate = (record?.createdAt || new Date().toISOString()).slice(
        0,
        10
      );
      await actions.updateEquipment(currentEquipment.id, {
        lastPmDate: pmDate,
      });
    }

    setEditingServiceRecordId(null);
    setEditServiceNote("");
    setEditServiceTag("");
  };

  const deleteServicePhoto = async (recordId, photoIndex) => {
    const confirmed = window.confirm("Delete this photo?");
    if (!confirmed) return;
    await actions.deleteServicePhoto(recordId, photoIndex);
  };

  // pendingSoftDelete holds the record id queued for the typed-confirm
  // modal. Replaces the old window.confirm path that was getting tripped
  // by accidental clicks / Enter (see TypedConfirmModal for the why).
  const [pendingSoftDelete, setPendingSoftDelete] = useState(null);
  const [softDeleteBusy, setSoftDeleteBusy] = useState(false);

  const softDeleteServiceRecord = (recordId) => {
    setPendingSoftDelete(recordId);
  };

  const confirmSoftDelete = async () => {
    const recordId = pendingSoftDelete;
    if (!recordId) return;
    setSoftDeleteBusy(true);
    try {
      await actions.softDeleteServiceRecord(recordId, user?.email);
      if (editingServiceRecordId === recordId) {
        setEditingServiceRecordId(null);
        setEditServiceNote("");
      }
    } finally {
      setSoftDeleteBusy(false);
      setPendingSoftDelete(null);
    }
  };

  const pendingSoftDeleteRecord = currentEquipment?.serviceRecords?.find(
    (r) => r.id === pendingSoftDelete
  );

  const restoreServiceRecord = async (recordId) => {
    const confirmed = window.confirm(
      "Restore this service record? It will return to the active history."
    );
    if (!confirmed) return;
    await actions.restoreServiceRecord(recordId, user?.email);
  };

  const permanentlyDeleteServiceRecord = async (recordId) => {
    const record = currentEquipment?.serviceRecords.find(
      (r) => r.id === recordId
    );
    const confirmed = window.confirm(
      `PERMANENTLY delete this service record?\n\n"${
        record?.notes?.slice(0, 40) || ""
      }${record?.notes?.length > 40 ? "..." : ""}"\n\nThis CANNOT be undone. Photos and files will also be erased.`
    );
    if (!confirmed) return;
    await actions.permanentlyDeleteServiceRecord(recordId, user?.email);
  };

  const addPartsListAttachments = async () => {
    if (
      partsListPhotoUpload.length === 0 &&
      partsListFileUpload.length === 0
    ) {
      return;
    }

    try {
      setUploadingPartsList(true);
      setErrorMessage("");

      const newPhotos = [];
      for (const file of partsListPhotoUpload) {
        const filePath = `organizations/${orgId}/partsListPhotos/${customer.id}/${currentLocation.id}/${currentEquipment.id}/${Date.now()}-${file.name}`;
        const photoRef = ref(storage, filePath);
        await uploadBytes(photoRef, file);
        const photoUrl = await getDownloadURL(photoRef);
        newPhotos.push({ url: photoUrl, path: filePath, name: file.name });
      }

      const newFiles = [];
      for (const file of partsListFileUpload) {
        const filePath = `organizations/${orgId}/partsListFiles/${customer.id}/${currentLocation.id}/${currentEquipment.id}/${Date.now()}-${file.name}`;
        const fileRef = ref(storage, filePath);
        await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(fileRef);
        newFiles.push({ url: fileUrl, path: filePath, name: file.name });
      }

      await actions.addPartsListAttachments(
        currentEquipment.id,
        newPhotos,
        newFiles
      );
      setPartsListPhotoUpload([]);
      setPartsListFileUpload([]);

      const photoInput = document.getElementById("parts-photo-input");
      if (photoInput) photoInput.value = "";
      const fileInput = document.getElementById("parts-file-input");
      if (fileInput) fileInput.value = "";
    } catch (error) {
      console.error("Error uploading parts list attachments:", error);
      setErrorMessage(error.message);
    } finally {
      setUploadingPartsList(false);
    }
  };

  const deletePartsListPhoto = async (photoIndex) => {
    const confirmed = window.confirm("Delete this parts list photo?");
    if (!confirmed) return;
    await actions.deletePartsListPhoto(currentEquipment.id, photoIndex);
  };

  const deletePartsListFile = async (fileIndex) => {
    const confirmed = window.confirm("Delete this parts list file?");
    if (!confirmed) return;
    await actions.deletePartsListFile(currentEquipment.id, fileIndex);
  };

  if (!currentEquipment) {
    return (
      <>
        <BackButton onClick={onBack || (() => setSelectedEquipment(null))} />
        <p style={{ fontSize: "13px", color: "#666", marginTop: "8px" }}>
          Loading equipment…
        </p>
      </>
    );
  }

  return (
    <>
      <BackButton onClick={onBack || (() => setSelectedEquipment(null))} />

      <h2>{currentEquipment?.type}</h2>

      {errorMessage && (
        <div style={{ color: "red", marginBottom: 12 }}>
          Error: {errorMessage}
        </div>
      )}


      <div
        style={{
          ...styles.card,
          maxWidth: "780px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "6px 16px",
        }}
      >
        <div>
          <strong>Category:</strong> {currentEquipment?.category || "-"}
        </div>
        <div>
          <strong>Type:</strong> {currentEquipment?.subType || "-"}
        </div>
        <div>
          <strong>Manufacturer:</strong> {currentEquipment?.manufacturer || "-"}
        </div>
        <div>
          <strong>Model Number:</strong> {currentEquipment?.modelNumber || "-"}
        </div>
        <div>
          <strong>Serial Number:</strong> {currentEquipment?.serialNumber || "-"}
        </div>
        <div>
          <strong>Start-Up Date:</strong>{" "}
          {formatDate(currentEquipment?.startupDate)}
        </div>
        <div>
          <strong>Last PM Date:</strong>{" "}
          {formatDate(currentEquipment?.lastPmDate)}
        </div>
        <div>
          <strong>Next PM Due:</strong>{" "}
          {getNextPmDate(
            currentEquipment?.lastPmDate,
            currentEquipment?.pmFrequencyDays
          )}
        </div>
        <div>
          <strong>PM Frequency:</strong>{" "}
          {currentEquipment?.pmFrequencyDays
            ? `${currentEquipment.pmFrequencyDays} days`
            : "-"}
        </div>
      </div>

      <div style={{ ...styles.card, maxWidth: "780px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <strong>Status:</strong>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 10px",
              borderRadius: "12px",
              background: statusMeta[currentStatus].bg,
              color: statusMeta[currentStatus].color,
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: statusMeta[currentStatus].color,
              }}
            />
            {statusMeta[currentStatus].label}
          </span>
          {canSetStatus && (
            <div style={{ display: "flex", gap: "6px", marginLeft: "auto" }}>
              {["green", "yellow", "red"].map((s) => (
                <button
                  key={s}
                  style={{
                    ...styles.button,
                    margin: 0,
                    background:
                      currentStatus === s ? statusMeta[s].bg : "#f3f4f6",
                    color:
                      currentStatus === s ? statusMeta[s].color : "#222",
                    fontWeight: currentStatus === s ? 600 : 500,
                    opacity: savingStatus ? 0.5 : 1,
                  }}
                  onClick={() => handleSetStatus(s)}
                  disabled={savingStatus}
                >
                  {statusMeta[s].label}
                </button>
              ))}
            </div>
          )}
        </div>
        {currentStatusNote && (
          <div
            style={{
              marginTop: "8px",
              fontSize: "13px",
              color: "#444",
              fontStyle: "italic",
            }}
          >
            "{currentStatusNote}"
          </div>
        )}
        {currentEquipment?.statusChangedAt && (
          <div
            style={{
              marginTop: "4px",
              fontSize: "12px",
              color: "#666",
            }}
          >
            Last set by{" "}
            {currentEquipment.statusChangedByName ||
              currentEquipment.statusChangedBy ||
              "—"}{" "}
            on{" "}
            {new Date(currentEquipment.statusChangedAt).toLocaleString()}
          </div>
        )}
        {statusError && (
          <div style={{ marginTop: "6px", color: "red", fontSize: "12px" }}>
            {statusError}
          </div>
        )}
      </div>

      <div style={{ ...styles.card, maxWidth: "780px" }}>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {canRequestService && (
            <button
              style={styles.button}
              onClick={() => setShowRequest((s) => !s)}
            >
              {showRequest ? "Cancel Request" : "Request Service"}
            </button>
          )}
          <button
            style={styles.button}
            onClick={() => setShowStartupInstructions(!showStartupInstructions)}
          >
            Startup / Reset Instructions
          </button>
          <button
            style={styles.button}
            onClick={() => setShowPartsList(!showPartsList)}
          >
            Parts List
          </button>
          {setWarrantiesForEquipment && (
            <button
              style={styles.button}
              onClick={() => setWarrantiesForEquipment(currentEquipment)}
            >
              Warranties
            </button>
          )}
        </div>

        {canRequestService && showRequest && (
          <div style={{ marginTop: "8px" }}>
            <p style={{ fontSize: "12px", color: "#666", margin: "0 0 6px" }}>
              Send your contractor a service request for this unit. It lands
              on their job board for them to accept.
            </p>
            <label
              style={{
                fontSize: "12px",
                display: "block",
                marginBottom: "4px",
              }}
            >
              What's wrong / what do you need?
            </label>
            <textarea
              style={styles.textarea}
              placeholder="Brief description (optional but helpful)"
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
            />

            <label
              style={{
                fontSize: "12px",
                display: "block",
                marginTop: "8px",
                marginBottom: "4px",
              }}
            >
              Send to
            </label>
            {reqContractorOrgIds.map((picked, i) => {
              const excluded = otherPicks(i);
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <select
                    value={picked || ""}
                    onChange={(e) => setContractorAt(i, e.target.value)}
                  >
                    <option value="">Select a contractor...</option>
                    {contractorOptions
                      .filter((c) => !excluded.has(c.orgId))
                      .map((c) => (
                        <option key={c.orgId} value={c.orgId}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                  {reqContractorOrgIds.length > 1 && (
                    <button
                      type="button"
                      style={{
                        ...styles.button,
                        color: "red",
                        padding: "2px 8px",
                      }}
                      onClick={() => removeContractorRow(i)}
                    >
                      x
                    </button>
                  )}
                </div>
              );
            })}
            {contractorOptions.length > reqContractorOrgIds.length && (
              <button
                type="button"
                style={{ ...styles.button, marginTop: "4px" }}
                onClick={addContractorRow}
                title="Send the same request to another contractor for a competitive response"
              >
                + Contractor
              </button>
            )}

            <div style={{ marginTop: "10px" }}>
              <button
                style={styles.button}
                onClick={requestService}
                disabled={
                  requesting ||
                  reqContractorOrgIds.filter(Boolean).length === 0
                }
              >
                {requesting ? "Sending..." : "Send Request"}
              </button>
            </div>
          </div>
        )}
        {canRequestService && requestMsg && (
          <p style={{ fontSize: "12px", marginTop: "6px" }}>{requestMsg}</p>
        )}

        {showStartupInstructions && (
          <div style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}>
            {currentEquipment?.startupInstructions || "-"}
          </div>
        )}

        {showPartsList && (
          <>
            <div style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}>
              {currentEquipment?.partsList || "-"}
            </div>

            {canEdit && (
              <div style={{ marginTop: "12px" }}>
                <label>Attach Photo: </label>
                {isCameraNative() ? (
                  // Single button that pops the native "Take Photo /
                  // Photo Library" chooser. Camera-first feel since the
                  // typical parts-list use case is snapping a current
                  // photo of a part; library is one tap away if needed.
                  <button
                    type="button"
                    style={{ ...styles.button, margin: 0 }}
                    disabled={uploadingPartsList}
                    onClick={async () => {
                      try {
                        const file = await takePhotoPromptNative();
                        if (file) {
                          setPartsListPhotoUpload((prev) => [
                            ...(prev || []),
                            file,
                          ]);
                        }
                      } catch {}
                    }}
                  >
                    📷 Add Photo
                  </button>
                ) : (
                  // Web fallback: capture="environment" makes the mobile
                  // browser open the rear camera first on phones that
                  // support it; desktops fall back to a file picker.
                  <input
                    id="parts-photo-input"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={(e) =>
                      setPartsListPhotoUpload(Array.from(e.target.files))
                    }
                    disabled={uploadingPartsList}
                  />
                )}
                <br />
                <label>Attach PDF / Docs: </label>
                <input
                  id="parts-file-input"
                  type="file"
                  accept="application/pdf"
                  multiple
                  onChange={(e) =>
                    setPartsListFileUpload(Array.from(e.target.files))
                  }
                  disabled={uploadingPartsList}
                />
                {(partsListPhotoUpload.length > 0 ||
                  partsListFileUpload.length > 0) && (
                  <p>
                    {partsListPhotoUpload.length} photo(s),{" "}
                    {partsListFileUpload.length} file(s) selected
                  </p>
                )}
                {uploadingPartsList && <p>Uploading...</p>}
                <button
                  style={styles.button}
                  onClick={addPartsListAttachments}
                  disabled={uploadingPartsList}
                >
                  Upload
                </button>
              </div>
            )}

            {(currentEquipment?.partsListFiles || []).length > 0 && (
              <div style={{ marginTop: "12px" }}>
                <strong>Documents:</strong>
                <ul>
                  {currentEquipment.partsListFiles.map((file, index) => (
                    <li key={index}>
                      <a href={file.url} target="_blank" rel="noreferrer">
                        {file.name || "View Document"}
                      </a>
                      {canEdit && (
                        <button
                          style={{ ...styles.button, marginLeft: "8px" }}
                          onClick={() => deletePartsListFile(index)}
                        >
                          Delete
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(currentEquipment?.partsListPhotos || []).length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginTop: "12px",
                }}
              >
                {currentEquipment.partsListPhotos.map((photo, index) => (
                  <div key={index}>
                    <a href={photo.url} target="_blank" rel="noreferrer">
                      <img
                        src={photo.url}
                        alt={`Parts ${index + 1}`}
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          display: "block",
                        }}
                      />
                    </a>
                    {canEdit && (
                      <button
                        onClick={() => deletePartsListPhoto(index)}
                        style={{ marginTop: "4px" }}
                      >
                        Delete Photo
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <h3>Add Service Record</h3>
      <div style={{ marginBottom: "8px" }}>
        <label>Service Type: </label>
        <select
          value={serviceTag}
          onChange={(e) => setServiceTag(e.target.value)}
        >
          <option value="">Select Type</option>
          {serviceTagOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <textarea
        style={{ ...styles.textarea, maxWidth: "780px" }}
        value={serviceNote}
        onChange={(e) => setServiceNote(e.target.value)}
        rows={6}
        placeholder="Service notes"
      />
      <br />

      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginTop: "8px" }}>
        {isCameraNative() ? (
          <>
            <button
              type="button"
              style={{ ...styles.button, margin: 0, lineHeight: "1.4", opacity: uploadingPhoto ? 0.5 : 1, cursor: uploadingPhoto ? "not-allowed" : "pointer" }}
              disabled={uploadingPhoto}
              onClick={async () => {
                try {
                  const file = await takePhotoNative();
                  if (file) setServicePhotos((prev) => [...prev, file]);
                } catch {
                  // User cancelled or denied permission — silent.
                }
              }}
              title="Take photo with camera"
            >
              📷 Camera
            </button>
            <button
              type="button"
              style={{ ...styles.button, margin: 0, lineHeight: "1.4", opacity: uploadingPhoto ? 0.5 : 1, cursor: uploadingPhoto ? "not-allowed" : "pointer" }}
              disabled={uploadingPhoto}
              onClick={async () => {
                try {
                  const files = await pickPhotosNative(10);
                  if (files.length) setServicePhotos((prev) => [...prev, ...files]);
                } catch {
                  // User cancelled or denied permission — silent.
                }
              }}
              title="Pick photos from gallery"
            >
              🖼️ Gallery
            </button>
          </>
        ) : (
          <>
            <label
              htmlFor="service-photo-input"
              style={{ ...styles.button, margin: 0, display: "inline-block", lineHeight: "1.4", opacity: uploadingPhoto ? 0.5 : 1, cursor: uploadingPhoto ? "not-allowed" : "pointer" }}
              title="Attach photos"
            >
              📷 Photo
            </label>
            <input
              id="service-photo-input"
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={(e) => setServicePhotos(Array.from(e.target.files))}
              disabled={uploadingPhoto}
              style={{ display: "none" }}
            />
          </>
        )}

        <label
          htmlFor="service-file-input"
          style={{ ...styles.button, margin: 0, display: "inline-block", lineHeight: "1.4", cursor: "pointer" }}
          title="Attach PDF / report"
        >
          📎 File
        </label>
        <input
          id="service-file-input"
          type="file"
          accept="application/pdf"
          multiple
          onChange={(e) => setServiceFiles(Array.from(e.target.files))}
          style={{ display: "none" }}
        />

        {(servicePhotos.length > 0 || serviceFiles.length > 0) && (
          <span style={{ fontSize: "12px", color: "#666" }}>
            {servicePhotos.length > 0 && `${servicePhotos.length} photo(s)`}
            {servicePhotos.length > 0 && serviceFiles.length > 0 && ", "}
            {serviceFiles.length > 0 && `${serviceFiles.length} file(s)`}
            {" selected"}
          </span>
        )}

        <button
          onClick={addServiceRecord}
          disabled={uploadingPhoto || !serviceTag}
          style={{
            ...styles.primaryButton,
            margin: 0,
            opacity: uploadingPhoto || !serviceTag ? 0.5 : 1,
            cursor: uploadingPhoto || !serviceTag ? "not-allowed" : "pointer",
          }}
          title={!serviceTag ? "Select a service type first" : ""}
        >
          {uploadingPhoto ? "Saving..." : "Add Record"}
        </button>
      </div>

      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "flex-end", marginTop: "12px" }}>
        <div>
          <h3 style={{ margin: "0 0 4px" }}>Search Service Notes</h3>
          <input
            placeholder="Search notes..."
            value={serviceSearch}
            onChange={(e) => setServiceSearch(e.target.value)}
            style={{ ...styles.input, width: "240px", margin: 0 }}
          />
        </div>
        <div>
          <h3 style={{ margin: "0 0 4px" }}>Filter by Type</h3>
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            style={{ ...styles.input, width: "200px", margin: 0 }}
          >
            <option value="">All</option>
            {serviceTagOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isAdmin && (
        <label style={{ display: "block", marginTop: "12px" }}>
          <input
            type="checkbox"
            checked={showDeletedRecords}
            onChange={(e) => setShowDeletedRecords(e.target.checked)}
          />
          {" "}Show deleted records
        </label>
      )}

      <h3>Service History</h3>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {[...(currentEquipment?.serviceRecords || [])]
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .filter((record) => {
            if (record.deleted && !showDeletedRecords) return false;
            const words = serviceSearch
              .toLowerCase()
              .split(" ")
              .filter((w) => w);
            const matchesSearch = words.every((word) =>
              (record.notes || "").toLowerCase().includes(word)
            );
            const matchesTag = !tagFilter || record.tag === tagFilter;
            return matchesSearch && matchesTag;
          })
          .map((record) => (
            <li
              key={record.id}
              style={{
                ...styles.card,
                maxWidth: "780px",
                opacity: record.deleted ? 0.55 : 1,
                background: record.deleted ? "#ffecec" : "#f9f9f9",
              }}
            >
              {editingServiceRecordId === record.id ? (
                <>
                  <div style={{ marginBottom: "8px" }}>
                    <label>Service Type: </label>
                    <select
                      value={editServiceTag}
                      onChange={(e) => setEditServiceTag(e.target.value)}
                    >
                      <option value="">Select Type</option>
                      {serviceTagOptions.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    style={styles.textarea}
                    value={editServiceNote}
                    onChange={(e) => setEditServiceNote(e.target.value)}
                    rows={4}
                    cols={40}
                  />
                  <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                    <button
                      onClick={updateServiceRecord}
                      style={{ ...styles.primaryButton, margin: 0 }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingServiceRecordId(null);
                        setEditServiceNote("");
                        setEditServiceTag("");
                      }}
                      style={{ ...styles.button, margin: 0 }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <strong>{record.date}</strong>{" "}
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    (by {record.createdByName || record.createdBy}
                    {record.createdByOrgName
                      ? ` · ${record.createdByOrgName}`
                      : ""}
                    )
                  </span>
                  {record.deleted && (
                    <span style={{ color: "red", marginLeft: "8px" }}>
                      DELETED
                    </span>
                  )}
                  <br />
                  <HighlightText
                    text={record.notes || ""}
                    search={serviceSearch}
                  />
                  <br />

                  {(record.files || []).length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      <strong>Documents:</strong>
                      <ul>
                        {record.files.map((file, index) => (
                          <li key={index}>
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {file.name || "View Document"}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(record.photos || []).length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        marginTop: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      {(record.photos || []).map((photo, index) => {
                        const photoUrl =
                          typeof photo === "string" ? photo : photo.url;
                        return (
                          <div key={index}>
                            <a
                              href={photoUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={photoUrl}
                                alt={`Service ${index + 1}`}
                                style={{
                                  width: "120px",
                                  height: "120px",
                                  objectFit: "cover",
                                  border: "1px solid #ccc",
                                  borderRadius: "6px",
                                  display: "block",
                                }}
                              />
                            </a>
                            {canEdit && !record.deleted && (
                              <button
                                onClick={() =>
                                  deleteServicePhoto(record.id, index)
                                }
                                style={{ marginTop: "4px" }}
                              >
                                Delete Photo
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {((canEdit && !record.deleted) || canViewAudit) && (
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginTop: "10px",
                      }}
                    >
                      {canEdit && !record.deleted && (
                        <button
                          style={{ ...styles.button, flex: 1, margin: 0 }}
                          onClick={() => {
                            setEditingServiceRecordId(record.id);
                            setEditServiceNote(record.notes);
                            setEditServiceTag(record.tag || "");
                          }}
                        >
                          Edit
                        </button>
                      )}
                      {canEdit && !record.deleted && (
                        <button
                          style={{ ...styles.button, flex: 1, margin: 0 }}
                          onClick={() => softDeleteServiceRecord(record.id)}
                        >
                          Delete
                        </button>
                      )}
                      {canViewAudit && (
                        <button
                          style={{ ...styles.button, flex: 1, margin: 0 }}
                          onClick={() =>
                            setShowAuditTrail((prev) => ({
                              ...prev,
                              [record.id]: !prev[record.id],
                            }))
                          }
                        >
                          {showAuditTrail[record.id]
                            ? "Hide Audit Trail"
                            : "Audit Trail"}
                        </button>
                      )}
                    </div>
                  )}

                  {canViewAudit && showAuditTrail[record.id] && (
                    <AuditTrail trail={record.auditTrail} />
                  )}

                  {isAdmin && record.deleted && (
                    <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                      <button
                        style={{
                          ...styles.button,
                          margin: 0,
                          background: "#e6f7ea",
                          color: "#1a7f37",
                        }}
                        onClick={() => restoreServiceRecord(record.id)}
                      >
                        Restore
                      </button>
                      <button
                        style={{
                          ...styles.button,
                          margin: 0,
                          background: "#fee",
                          color: "#a00",
                        }}
                        onClick={() =>
                          permanentlyDeleteServiceRecord(record.id)
                        }
                      >
                        Permanently Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
      </ul>

      <TypedConfirmModal
        open={!!pendingSoftDelete}
        title="Delete service record?"
        message={(() => {
          const notes = pendingSoftDeleteRecord?.notes || "";
          const preview =
            notes.length > 80 ? `${notes.slice(0, 80)}...` : notes || "(no notes)";
          return `This will move the record to Deleted Items. It will NOT be permanently erased and can be restored from there.\n\n"${preview}"`;
        })()}
        confirmWord="DELETE"
        confirmLabel="Delete record"
        busy={softDeleteBusy}
        onConfirm={confirmSoftDelete}
        onCancel={() => !softDeleteBusy && setPendingSoftDelete(null)}
      />
    </>
  );
}
